import React from 'react';
import { useReducer } from 'react';

import axios from 'axios';

import mainReducer from '../../containers/Main/mainReducer';
import {
    fomrTemplateUrl,
    fomrInstanceUrl,
    formPutUrl,
    formSubmitUrl,
    fomrInstanceLocalUrl,
    formUrl,
    enumUrl,
} from '../../services/endpoints';
import getCookie from '../../services/cookieHelper';

function useMainDataManager() {
    const windowUrl = window.location.search;
    const params = React.useMemo(() => new URLSearchParams(windowUrl), [windowUrl]);
    const token = React.useMemo(() => getCookie('a_t'), []);
    const templateId = React.useMemo(() => params.get('templateId'), [params]);
    const formGlobalId = React.useMemo(() => params.get('id'), [params]);
    const formInstanceId = React.useMemo(() => params.get('formId'), [params]);
    const urlRegion = React.useMemo(() => params.get('region'), [params]);

    const [
        {
            formTemplateData,
            formInstanceData,
            loading,
            loadingCountry,
            posting,
            hasErrored,
            error,
            countryList,
            selectedCountry,
        },
        dispatch,
    ] = useReducer(mainReducer, {
        loading: true,
        loadingCountry: true,
        posting: false,
        formTemplateData: {},
        formInstanceData: {},
        prevFields: [],
        hasErrored: false,
        error: '',
        token: token,
        formGlobalId: formGlobalId,
        submitError: '',
        countryList: [],
        selectedCountry: null,
    });

    const signal = React.useRef(axios.CancelToken.source());

    const setSearchRequest = React.useCallback(() => {
        dispatch({ type: 'searchRequest' });
        let url = `${fomrTemplateUrl}/${templateId}?timestamp=${new Date().getTime()}`;
        axios
            .get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': 'en',
                    AUTHORIZATION: `BEARER ${token}`,
                    'CL-App-Region': urlRegion,
                },
                cancelToken: signal.current.token,
            })
            .then(function (response) {
                setSearchSuccess(response.data);
            })
            .catch(function (error) {
                dispatch({ type: 'errored', error: error });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, token]);

    function setSearchSuccess(options: any) {
        dispatch({ type: 'searchSuccess', data: options });
    }

    const fetchFormRequest = React.useCallback((formGlobalId: string): void => {
        dispatch({ type: 'fetchFormRequest' });
        let url = `${fomrInstanceUrl}/${formGlobalId}?timestamp=${new Date().getTime()}`;
        axios
            .get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': 'en',
                    'CL-App-Region': urlRegion,
                },
                cancelToken: signal.current.token,
            })
            .then(function (response) {
                fetchFormSuccess(response.data);
            })
            .catch(function (error) {
                dispatch({ type: 'errored', error: error });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchLocalFormRequest = React.useCallback((formInstanceId: string): void => {
        dispatch({ type: 'fetchFormRequest' });
        let url = `${fomrInstanceLocalUrl}/${formInstanceId}?timestamp=${new Date().getTime()}`;
        axios
            .get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': 'en',
                    'CL-App-Region': urlRegion,
                },
                cancelToken: signal.current.token,
            })
            .then(function (response) {
                fetchFormSuccess(response.data);
            })
            .catch(function (error) {
                dispatch({ type: 'errored', error: error });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function fetchFormSuccess(options: any) {
        dispatch({ type: 'fetchFormSuccess', data: options });
    }

    const fetchCountries = React.useCallback(
        (formInstanceId: string, isFormPreview: boolean): void => {
            dispatch({ type: 'fetchCountriesRequest' });
            let url = isFormPreview
                ? `${enumUrl}?timestamp=${new Date().getTime()}`
                : `${formUrl}/${formInstanceId}/countrylist?timestamp=${new Date().getTime()}`;
            axios
                .get(url, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': 'en',
                        'CL-App-Region': urlRegion,
                    },
                    cancelToken: signal.current.token,
                })
                .then(function (response) {
                    const countryList = isFormPreview ? response.data.countryList : response.data;
                    fetchCountriesSuccess(countryList);
                })
                .catch(function (error) {
                    dispatch({ type: 'errored', error: error });
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    function fetchCountriesSuccess(options: any) {
        dispatch({ type: 'fetchCountriesSuccess', data: options });
    }

    const putFormRequest = React.useCallback((formFieldId: number, value: any) => {
        dispatch({ type: 'putFormRequest', data: { fieldId: formFieldId, value } });
    }, []);

    const putSelectedCountry = React.useCallback((value: any) => {
        dispatch({ type: 'putSelectedCountry', data: value });
    }, []);

    const putFormChange = React.useCallback(
        (formId: string, formFieldId: number, value: any): Promise<any> => {
            return new Promise<void>((resolve, reject) => {
                const url = formPutUrl;
                axios
                    .put(
                        url,
                        {
                            formId,
                            formFieldId,
                            value,
                        },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept-Language': 'en',
                                'CL-App-Region': urlRegion,
                            },
                            cancelToken: signal.current.token,
                        },
                    )
                    .then(function () {
                        putFormSuccess({ fieldId: formFieldId, value: value });
                        resolve();
                    })
                    .catch(function (error) {
                        putFormError();
                        reject();
                    });
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    function putFormError() {
        dispatch({ type: 'putFormError' });
    }

    function putFormSuccess(data) {
        dispatch({ type: 'putFormSuccess', data });
    }

    function submitFormSuccess() {
        dispatch({ type: 'submitFormSuccess' });
    }

    const submitForm = React.useCallback((formId) => {
        dispatch({ type: 'submitFormRequest' });
        return new Promise<void>((resolve, reject) => {
            const url = formSubmitUrl;
            axios
                .put(
                    url,
                    {
                        formId,
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept-Language': 'en',
                            'CL-App-Region': urlRegion,
                        },
                        cancelToken: signal.current.token,
                    },
                )
                .then(function () {
                    // if this is being display inside of an iFrame, we will dispatch an event for the parent to know
                    if (window.parent) {
                        // parent.postMessage("message to be sent", "http://the-website-that-will-receive-the-msg.com")
                        window.parent.postMessage('formSubmitted', '*');
                    }
                    submitFormSuccess();
                    resolve();
                })
                .catch(function (error) {
                    dispatch({ type: 'errored', error: error });
                    reject();
                });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return {
        loading,
        loadingCountry,
        posting,
        hasErrored,
        error,
        formTemplateData,
        formInstanceData,
        token,
        templateId,
        formGlobalId,
        formInstanceId,
        setSearchRequest,
        fetchFormRequest,
        fetchLocalFormRequest,
        fetchFormSuccess,
        putFormRequest,
        fetchCountries,
        putSelectedCountry,
        signal,
        putFormChange,
        submitForm,
        countryList,
        selectedCountry,
    };
}
export default useMainDataManager;
